import { ReactElement, ReactNode } from 'react';
import { cn } from '@/helper/cn';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  icon?: ReactElement | string;
  onClick?: () => void;
  variant?: 'solid' | 'outline' | 'text';
  color?: 'green' | 'orange' | 'black' | 'gray' | 'blue' | 'purple';
  className?: string;
  type?: 'button' | 'reset' | 'submit';
  hideLabel?: boolean;
  mini?: boolean;
  trailing?: ReactNode;
  component?: React.ElementType | 'button';
  to?: string;
}

const buttonVariants = {
  solid: {
    green: 'bg-brandPrimaryGreen text-white border-brandPrimaryGreen hover:bg-transparent hover:text-brandPrimaryGreen',
    orange: 'bg-buttonOrange text-white border-buttonOrange hover:bg-transparent hover:text-buttonOrange',
    black: 'bg-brandNeutralBlack text-white border-brandNeutralBlack hover:bg-transparent hover:text-brandNeutralBlack',
    gray: 'bg-brandNeutralGray text-white border-brandNeutralGray hover:bg-transparent hover:text-brandNeutralGray',
    blue: 'bg-buttonBlue text-white border-buttonBlue hover:bg-transparent hover:text-buttonBlue',
    purple: 'bg-buttonPurple text-white border-buttonPurple hover:bg-transparent hover:text-buttonPurple'
  },
  outline: {
    green: 'bg-transparent text-brandPrimaryGreen border-brandPrimaryGreen hover:bg-brandPrimaryGreen hover:text-white',
    orange: 'bg-transparent text-buttonOrange border-buttonOrange hover:bg-buttonOrange hover:text-white',
    black:
      'bg-transparent text-brandNeutralBlack border-brandNeutralBlack/30 hover:bg-brandNeutralBlack hover:text-white',
    gray: 'bg-transparent text-brandNeutralGray border-brandNeutralGray/30 hover:bg-brandNeutralGray hover:text-white',
    blue: 'bg-transparent text-buttonBlue border-buttonBlue/30 hover:bg-buttonBlue hover:text-white',
    purple:
      'bg-transparent text-buttonPurple border-buttonBltext-buttonPurple/30 hover:bg-buttonBltext-buttonPurple hover:text-white'
  },
  text: {
    black:
      'bg-transparent text-brandNeutralBlack border-transparent hover:bg-transparent hover:border-brandNeutralBlack'
  }
} as const;

export function Button({
  label,
  icon,
  onClick,
  variant = 'solid',
  color = 'orange',
  className,
  type = 'button',
  hideLabel = false,
  disabled = false,
  mini = false,
  component = 'button',
  to,
  trailing
}: ButtonProps) {
  const Component = component || 'button';
  const buttonProps = to ? { to } : { type, onClick: onClick ?? undefined };
  const buttonVariant = buttonVariants[variant];
  const buttonStyle = buttonVariant[color as keyof typeof buttonVariant];

  return (
    <Component
      disabled={disabled}
      {...buttonProps}
      className={cn(
        'group flex items-center justify-center gap-2 border text-lg rounded-lg px-4 py-2 font-semibold transition-all duration-200 overflow-hidden whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed',
        buttonStyle,
        className,
        { 'justify-between': trailing }
      )}
    >
      {icon && (typeof icon === 'string' ? <img src={icon} alt="" className="w-5 h-5" /> : icon)}

      {label && (
        <span
          className={cn('transition-all duration-200 leading-6 max-w-xs opacity-100', {
            'max-w-0 opacity-0 group-hover:max-w-xs group-hover:opacity-100': hideLabel,
            'text-sm': mini
          })}
        >
          {label}
        </span>
      )}

      {trailing && trailing}
    </Component>
  );
}
